import React from 'react';
import { ArrowLeft, ShoppingBag, ShoppingCart, Heart, Search, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Template 10: DAWN - Minimalist, Clean, Modern (Inspired by Shopify's Dawn theme)
const Template10 = () => {
    const navigate = useNavigate();

    return (
        <div className="bg-white min-h-screen font-sans">
            {/* Header with Navigation */}
            <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
                <div className="container mx-auto px-4 lg:px-8">
                    <div className="flex justify-between items-center py-4 lg:py-5">
                        <h1 onClick={() => navigate('/template10')} className="text-2xl lg:text-3xl font-bold text-gray-900 cursor-pointer tracking-tight">
                            DAWN
                        </h1>
                        <div className="flex items-center gap-4 lg:gap-6">
                            <Search className="w-5 h-5 text-gray-900 cursor-pointer hover:text-gray-600 transition-colors" />
                            <Heart className="w-5 h-5 text-gray-900 cursor-pointer hover:text-gray-600 transition-colors" />
                            <div className="relative cursor-pointer" onClick={() => navigate('/template10/cart')}>
                                <ShoppingCart className="w-5 h-5 text-gray-900 hover:text-gray-600 transition-colors" />
                                <span className="absolute -top-2 -right-2 bg-gray-900 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-medium">0</span>
                            </div>
                            <User className="w-5 h-5 text-gray-900 cursor-pointer hover:text-gray-600 transition-colors" onClick={() => navigate('/template10/profile')} />
                        </div>
                    </div>
                    <nav className="border-t border-gray-200">
                        <div className="flex justify-center items-center gap-8 lg:gap-12 py-4">
                            <button onClick={() => navigate('/template10')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Home</button>
                            <button onClick={() => navigate('/template10/shop')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Shop</button>
                            <button onClick={() => navigate('/template10/contact')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Contact</button>
                            <button onClick={() => navigate('/template10/orders')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Orders</button>
                        </div>
                    </nav>
                </div>
            </header>

            {/* Hero Section - Large Image with Text Overlay */}
            <section className="relative w-full min-h-[60vh] lg:min-h-[80vh] bg-gray-100 overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src="https://placehold.co/1920x1080/E0E0E0/333333?text=Hero+Image"
                        alt="Hero"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent"></div>
                </div>
                <div className="relative h-full min-h-[60vh] lg:min-h-[80vh] flex items-center justify-center text-center px-4">
                    <div className="max-w-2xl">
                        <h2 className="text-5xl md:text-6xl font-light text-white mb-6 tracking-tight">
                            New Collection
                        </h2>
                        <p className="text-xl text-white/90 mb-8 font-light">
                            Discover our latest arrivals
                        </p>
                        <button className="px-8 py-3 bg-white text-gray-900 text-sm font-medium hover:bg-gray-100 transition-colors">
                            Shop Now
                        </button>
                    </div>
                </div>
            </section>

            {/* Featured Categories - Clean Grid */}
            <section className="max-w-7xl mx-auto px-4 py-16">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {[
                        { title: 'Women', image: 'https://images.unsplash.com/photo-1483985988355-763728e1935b?w=600&h=800&fit=crop' },
                        { title: 'Men', image: 'https://images.unsplash.com/photo-1490114538077-0a7f8cb49891?w=600&h=800&fit=crop' },
                        { title: 'Accessories', image: 'https://images.unsplash.com/photo-1492707892479-7bc8d5a4ee93?w=600&h=800&fit=crop' }
                    ].map((category, index) => (
                        <div key={index} className="group relative overflow-hidden bg-gray-100 aspect-[3/4] cursor-pointer">
                            <img
                                src={category.image}
                                alt={category.title}
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors"></div>
                            <div className="absolute bottom-8 left-8 text-white">
                                <h3 className="text-2xl font-light mb-2">{category.title}</h3>
                                <span className="text-sm underline underline-offset-4">Shop Collection</span>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Product Grid */}
            <section className="w-full bg-gray-50 py-16 lg:py-24">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-4">All Products</h2>
                        <p className="text-xl text-gray-600">Discover our latest collection</p>
                    </div>

                    <div className="w-full max-w-none">
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 lg:gap-8">
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(id => (
                                <div key={id} className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200 hover:shadow-2xl hover:-translate-y-2 hover:border-gray-900 transition-all duration-300 group">
                                    <div className="aspect-[4/5] overflow-hidden relative bg-gray-50">
                                        <img
                                            src={`https://placehold.co/400x500/F0F0F0/333333?text=Product+${id}`}
                                            alt={`Product ${id}`}
                                            className="object-cover object-center w-full h-full group-hover:scale-110 transition-transform duration-700"
                                        />
                                    </div>
                                    <div className="p-4 lg:p-6">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">Product Title {id}</h3>
                                        <p className="text-xl lg:text-2xl font-bold text-gray-900 mb-4">${(69 + id * 10)}.00</p>
                                        <button className="w-full bg-gray-900 text-white py-3 px-6 rounded-lg font-medium hover:bg-gray-800 transition-all duration-300">
                                            Add to Cart
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Newsletter */}
            <section className="bg-gray-50 py-16">
                <div className="max-w-2xl mx-auto text-center px-4">
                    <h3 className="text-2xl font-light text-gray-900 mb-4">Stay Updated</h3>
                    <p className="text-gray-600 mb-8">Subscribe to get special offers and updates</p>
                    <div className="flex gap-4 max-w-md mx-auto">
                        <input
                            type="email"
                            placeholder="Email address"
                            className="flex-1 px-4 py-3 border border-gray-300 focus:outline-none focus:border-gray-900 transition-colors"
                        />
                        <button className="px-6 py-3 bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 transition-colors">
                            Subscribe
                        </button>
                    </div>
                </div>
            </section>

            {/* Footer */}
                        {/* Footer */}
            <footer className="bg-gray-900 text-white mt-0">
                <div className="container mx-auto px-4 lg:px-8 py-12 lg:py-16">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8 lg:gap-12">
                        <div>
                            <h3 className="text-2xl font-bold mb-4 tracking-tight">VOGUE</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">Contemporary design excellence.</p>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide">Quick Links</h4>
                            <ul className="space-y-2 text-sm">
                                <li><span onClick={() => navigate('/template10')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Home</span></li>
                                <li><span onClick={() => navigate('/template10/shop')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Shop</span></li>
                                <li><span onClick={() => navigate('/template10/contact')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Contact</span></li>
                                <li><span onClick={() => navigate('/template10/orders')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Orders</span></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide">Customer Service</h4>
                            <ul className="space-y-2 text-sm">
                                <li className="text-gray-400">Shipping & Returns</li>
                                <li className="text-gray-400">Privacy Policy</li>
                                <li className="text-gray-400">Terms & Conditions</li>
                                <li className="text-gray-400">FAQ</li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide">Connect</h4>
                            <p className="text-gray-400 text-sm">hello@vogue.com</p>
                        </div>
                    </div>
                    <div className="border-t border-gray-800 mt-12 pt-8">
                        <p className="text-center text-gray-500 text-sm">© 2025 VOGUE. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Template10;
